/* eslint-disable max-len */
import React, {
    useCallback,
    useEffect,
    useState,
    useContext,
    useMemo
} from 'react';
import { useAuthentication, withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import PolChangeTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PolChangeTrackEvent.jsx';
import { MLProductCodeUtil, MLEndorsementFlowUtil, MLEndorsementUtil } from 'ml-portals-util-js';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { MLEmployersAffiliationsComponent } from 'ml-components-common-react';
import { MLNavigationStepsTrackingService } from 'ml-services-common-react';
// eslint-disable-next-line import/no-unresolved

import { Identify  } from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/TrackEvent';
import appConfig from 'app-config';
import metadata from './AffiliationPagePolChange.metadata.json5';
import messages from './AffiliationPagePolChange.messages';
import styles from './AffiliationPagePolChange.module.scss';
import { ScrollToError } from '@jutro/legacy/wizard-next';


const QUESTIONS_TO_SHOW = [
    'HOPOccupationType_Ext',
    'HOPEducationLevel_ML'
];


function AffiliationPagePolChange(props) {
    const {
        wizardData,
        updateWizardData,
        goNext,
        currentStepIndex,
        steps,
        changeNextSteps,
        currentStep
    } = props;
    const { submissionVM } = wizardData;
    const [enforceSearch, setEnforceSearch] = useState(false);
    const [showErrors, setShowErrors] = useState(false)
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const gafPathAspects = 'baseData.gafInfo_ML.aspects';
    const translator = useTranslator();
    const { EndorsementService } = useDependencies('EndorsementService');
    const viewModelService = useContext(ViewModelServiceContext);
    const { authHeader } = useAuthentication();
    const productCode = _.get(submissionVM, 'baseData.productCode.value', '');
    const isGPA = appConfig.portalCode === 'GPA';
    const [disableNextButton, setDisableNextButton] = useState(false);


    const {
        showProgressDialog
    } = MLModalService(); 

    const {
        addWarningAlert,
        getNumberOfAlerts
    } = MLNotificationBlockUtil();

    const {
        onValidate,
        isComponentValid,
        registerComponentValidation
    } = useValidation('AffiliationPage');

    const onUpdateSearchVal = useCallback((val) => {
        setEnforceSearch(val);
    }, []);

    const getNextStepText = () => {
        return (
            <FormattedMessage
                {...messages.nextBaseDescription}
                values={{
                    bold: (
                        <strong>{translator(messages.nextDescriptionBold)}</strong>
                    )
                }}
            />
        );
    };

    const areQuestionsInvalid = useCallback(() => {
        if (MLProductCodeUtil.isHomeOwners(productCode) || MLProductCodeUtil.isHomeOwnersPartialTypeRental(productCode)) {
            const answers = _.get(submissionVM, 'lobData.homeowners.dwellingAdditionalInfo.value[0].answers', {});
            return QUESTIONS_TO_SHOW.some((qName) => _.isUndefined(answers[qName]) || _.isEmpty(answers[qName]));
        }
        return false;
    }, [productCode, submissionVM]);

    const validateAffiliationFields = useCallback(() => {
        const gafVmAspects = _.get(submissionVM, gafPathAspects, {});
        if ((!_.isEmpty(gafVmAspects) && (!gafVmAspects.valid || !gafVmAspects.subtreeValid)) || areQuestionsInvalid()) {
            return false;
        }
        return true;
    }, [areQuestionsInvalid, submissionVM]);

    const isPaymentMethodPayrollDeduct = useMemo(() => {
        return _.get(submissionVM, 'paymentDetails_ML.paymentMethod.value') === 'payrolldeduct_ml';
    }, [submissionVM]);

    useEffect(() => {
        Identify('Endorsement Step Reached', {step: 'Employer/Affiliation Screen'}, submissionVM, authHeader)
    }, [])
    
    useEffect(() => {
        const payrollDeductWarningMessage = 'payrollDeductWarningMessage';
        if(isPaymentMethodPayrollDeduct && getNumberOfAlerts(payrollDeductWarningMessage) <= 0) {
            addWarningAlert(messages.payrollDeductWarning, payrollDeductWarningMessage);
        }
    }, [addWarningAlert, getNumberOfAlerts, isPaymentMethodPayrollDeduct]);

    useEffect(() => {
        MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.updatedEmployerAffiliation), false, currentStep.mlStepName, authHeader);
        registerComponentValidation(validateAffiliationFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

     useEffect(() => {
         if (isGPA) {
             PolChangeTrackEvent('Endorsement Step Reached', false, submissionVM, authHeader, { step: 'PolChange Affiliation Screen' });
         }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateWizardSubmissionData = useCallback((vm) => {
        _.set(wizardData, 'submissionVM', vm);
        updateWizardData(wizardData);
    }, [updateWizardData, wizardData]);

    const onNext = useCallback(async () => {
        if (!(submissionVM.aspects.valid && submissionVM.aspects.subtreeValid)) {
            setShowErrors(true)
            setErrorTimestamp(errorTimestamp+1);
            return;
        }

        const promise = EndorsementService.saveEndorsement([submissionVM.value], authHeader);
        showProgressDialog(messages.savingPolicyChange, promise);
        promise.then((updatedPolicyChange) => {
            const policyChange = new PolicyChange(Object.assign({}, updatedPolicyChange));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
            updateWizardSubmissionData(updatedEndorsementVM);
            MLNavigationStepsTrackingService.completeCurrentStep(null, authHeader);
            const lbl = MLEndorsementUtil.stepLabel(currentStep);
            MLEndorsementUtil.setEndorsementDescription(lbl, updatedEndorsementVM, translator);
            MLTagManagerService.sendFormStepPolicyChangeStep();
            MLEndorsementFlowUtil.addAndNavigateToSelectedStep('endorsementAdditionalChanges', steps, currentStepIndex, changeNextSteps, goNext, productCode)
        });

        if (isGPA) {
            PolChangeTrackEvent('Endorsement Step Submitted', false, submissionVM, authHeader, {
                step: 'PolChange Affiliation Screen',
                action: 'Affiliation Submitted'
            })
        }

    }, [EndorsementService, authHeader, changeNextSteps, currentStep, currentStepIndex, errorTimestamp, goNext, productCode, showProgressDialog, steps, submissionVM.aspects.subtreeValid, submissionVM.aspects.valid, submissionVM.value, translator, updateWizardSubmissionData, viewModelService]);

    const resolvers = {
        resolveComponentMap: {
            mlemployersaffiliationscomponent: MLEmployersAffiliationsComponent
        },
        resolveCallbackMap: {
            onValidate: onValidate,
            onUpdateSearchVal: onUpdateSearchVal,
            onNext: onNext
        },
        resolveClassNameMap: styles
    };

    const overrides = {
        gpaPageHeader: {
            visible: isGPA
        },
        contentContainer: {
            className: isGPA ? 'page-content-gpa' : ''
        },
        affiliationContentHeader: {
            visible: isGPA
        },
        employersAffiliationsComponent: {
            model: submissionVM,
            updateWizardData: updateWizardSubmissionData,
            isEndorsement: true,
            showErrors,
            setDisableNextButton: setDisableNextButton,
            isCurrentPayPlanPayroll: () => _.get(submissionVM, 'paymentDetails_ML.paymentMethod.value') === 'payrolldeduct_ml',
        },
        nextDescription: {
            content: getNextStepText()
        },
        stickyContinueSection: {
            defaultContinueSectionId: 'footer',
            nextDescription: getNextStepText(),
            onNext,
            visible: isGPA,
            disableNext: disableNextButton
        },
        nextButton: {
            disabled: disableNextButton
        }
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            policyChangePageHeaderML={isGPA ? messages.makeChanges : messages.gpaSectionHeader}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                model={submissionVM}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
                showErrors={showErrors}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
};

AffiliationPagePolChange.propTypes = wizardProps;
export default withAuthenticationContext(AffiliationPagePolChange);
